import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import { ChevronDown } from 'lucide-react';
import { motion, useReducedMotion } from 'motion/react';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { route } from 'ziggy-js';
import Button from '@/Components/Marketing/Button';
import { EASE_MK } from '@/Components/Marketing/easing';
import { FOCUS_RING, RING_SURFACE } from '@/Components/Marketing/focusRing';
import { CONSULTATION_URL, currentPathname, demoHref, isCurrent, pathnameOf } from '@/Components/Marketing/links';
import Logo from '@/Components/Marketing/Logo';
import SmartLink from '@/Components/Marketing/SmartLink';
import scrollToAnchor from '@/Components/Marketing/scrollToAnchor';
import { cn } from '@/lib/utils';

export const MOBILE_MENU_ID = 'marketing-mobile-menu';
export const RESOURCES_MENU_ID = 'marketing-resources-menu';

// Tailwind's lg breakpoint, where the inline nav replaces the mobile sheet.
const DESKTOP_QUERY = '(min-width: 1024px)';

// The wordmark's letters sit in the top two thirds of its box (the "y"
// descender fills the rest), so centring the box leaves the letters riding
// high against the nav labels; 4px down lines their centres up.
const LOGO_SIZE = 'h-7 translate-y-1';

// How long the pointer may be outside the Resources trigger and panel before
// a hover-opened panel closes: long enough to cross from one to the other.
const HOVER_CLOSE_DELAY = 150;

// The bar is 85% white: it floats over every band a page has (the canvas,
// white, the orange bands, the night ones, R87), and at 85% its mk-body nav
// labels keep AA over all of them (5.5:1 over night; 75% gave 4.3:1). Over
// those bands the glass is darker than white (2.3:1 for orange over night),
// so the bar's focus rings are two-tone (focusRing.js), its call to
// action's too. (The dark bar went with the last dark page, Revision 1.)
export const GLASS = `bg-white/85 ring-1 ring-mk-line backdrop-blur-xl shadow-mk-soft ${RING_SURFACE.glass}`;

// The sheet is solid: opened over a night or orange band, any see-through
// would pull its mk-subtle group labels under AA (and so there is nothing
// behind it to blur). Its rings, in a portal outside the glass, are orange
// on white, 3.17:1.
export const SHEET = 'bg-white text-mk-ink';

// Nav items: the body colour at rest, ink on hover, and a faint filled box
// for the page you are on.
const NAV_ITEM = {
    rest: 'text-mk-body hover:bg-mk-ink/[0.04] hover:text-mk-ink',
    current: 'bg-mk-ink/[0.05] text-mk-ink',
};

const NAV_ITEM_BASE =
    'inline-flex h-9 items-center gap-1 whitespace-nowrap rounded-md px-3.5 text-sm font-medium transition-colors duration-300 ease-mk';

/**
 * A top-level item's aria-current: 'page' on its own page, 'true' on a page
 * that lives under its path (the battery passport explainer, at
 * /consulting/battery-passport, under Consulting), so the bar shows the
 * section you are in; nothing anywhere else.
 */
export function sectionCurrent(href, currentPath) {
    if (isCurrent(href, currentPath)) return 'page';
    const path = pathnameOf(href);

    return path && path !== '/' && currentPath?.startsWith(`${path}/`) ? 'true' : undefined;
}

/**
 * What the bar's call to action books, by the page's `cta`: a demo, unless
 * the page is the consulting division's own (/consulting), where it books a
 * consultation. Each label keeps its one target site-wide (links.js).
 */
const CTAS = {
    demo: { label: 'site.cta.demo', href: demoHref },
    consultation: { label: 'site.cta.consultation', href: () => CONSULTATION_URL },
};

/**
 * The floating glass bar every marketing page opens with. Desktop: logo,
 * a centred nav with a Resources dropdown, Log in and the call to action
 * (`cta`: 'demo', the default, or 'consultation'). Below 1024px: a
 * hamburger that opens a full-screen sheet.
 */
export default function Header({ canLogin = true, cta = 'demo' }) {
    const { formatMessage } = useIntl();
    const t = id => formatMessage({ id });
    const [menuOpen, setMenuOpen] = useState(false);
    const currentPath = currentPathname();

    const homeHref = route('outer.welcome');

    // Home leads (2026-10-13: an item of its own, beside the logo's link).
    // The battery passport explainer has no item of its own: it lives under
    // Consulting, which the bar marks while you read it (sectionCurrent).
    const primary = [
        { key: 'home', label: t('site.nav.homeLink'), href: homeHref },
        { key: 'platform', label: t('site.nav.platform'), href: route('outer.platform') },
        { key: 'partners', label: t('site.nav.partners'), href: route('outer.partnerships') },
        { key: 'consulting', label: t('site.nav.consulting'), href: route('outer.consulting') },
    ];

    const resources = [
        {
            key: 'calendar',
            label: t('site.nav.calendar'),
            description: t('site.nav.calendarDescription'),
            href: route('outer.batterycompliancecalendar'),
        },
        {
            key: 'guides',
            label: t('site.nav.guides'),
            description: t('site.nav.guidesDescription'),
            href: route('outer.resources'),
        },
    ];

    const loginHref = canLogin ? route('login') : null;
    const action = CTAS[cta] ?? CTAS.demo;
    const actionLink = { label: t(action.label), href: action.href() };

    // Anchor scrolling, on first paint and on every later hash change.
    useEffect(() => {
        let cancelScroll = scrollToAnchor();

        const handleHashChange = () => {
            cancelScroll?.();
            cancelScroll = scrollToAnchor();
        };

        window.addEventListener('hashchange', handleHashChange);

        return () => {
            cancelScroll?.();
            window.removeEventListener('hashchange', handleHashChange);
        };
    }, []);

    // The sheet is a below-1024px thing. If the viewport grows past that while
    // it is open (a tablet rotating), CSS hides it, but the dialog would keep
    // its scroll lock, inert page and focus trap: close it instead.
    useEffect(() => {
        if (!menuOpen || typeof window.matchMedia !== 'function') return undefined;

        const desktop = window.matchMedia(DESKTOP_QUERY);
        const handleChange = event => {
            if (event.matches) setMenuOpen(false);
        };

        if (desktop.addEventListener) desktop.addEventListener('change', handleChange);
        else desktop.addListener?.(handleChange);

        return () => {
            if (desktop.removeEventListener) desktop.removeEventListener('change', handleChange);
            else desktop.removeListener?.(handleChange);
        };
    }, [menuOpen]);

    const navLinkClass = href => cn(NAV_ITEM_BASE, NAV_ITEM[sectionCurrent(href, currentPath) ? 'current' : 'rest'], FOCUS_RING);

    return (
        <header className="fixed inset-x-0 top-4 z-40 px-4 sm:px-6">
            <div
                className={cn(
                    'mx-auto flex h-16 max-w-[1180px] items-center gap-4 rounded-xl pl-5 pr-3',
                    'lg:grid lg:grid-cols-[1fr_auto_1fr]',
                    GLASS,
                )}
            >
                <SmartLink
                    href={homeHref}
                    aria-label={t('site.nav.home')}
                    className={cn('-mx-1.5 justify-self-start rounded-md px-1.5 py-1', FOCUS_RING)}
                >
                    <Logo className={LOGO_SIZE} />
                </SmartLink>

                <nav aria-label={t('site.nav.label')} className="hidden lg:block">
                    <ul role="list" className="flex items-center gap-1">
                        {primary.map(item => (
                            <li key={item.key}>
                                <SmartLink
                                    href={item.href}
                                    aria-current={sectionCurrent(item.href, currentPath)}
                                    className={navLinkClass(item.href)}
                                >
                                    {item.label}
                                </SmartLink>
                            </li>
                        ))}
                        <li>
                            <ResourcesMenu label={t('site.nav.resources')} items={resources} currentPath={currentPath} />
                        </li>
                    </ul>
                </nav>

                <div className="ml-auto flex items-center gap-2 lg:ml-0 lg:justify-self-end">
                    {loginHref && (
                        <SmartLink
                            href={loginHref}
                            className={cn(NAV_ITEM_BASE, NAV_ITEM.rest, FOCUS_RING, 'hidden lg:inline-flex')}
                        >
                            {t('site.nav.login')}
                        </SmartLink>
                    )}
                    <Button href={actionLink.href} className="hidden sm:inline-flex">
                        {actionLink.label}
                    </Button>
                    <button
                        type="button"
                        onClick={() => setMenuOpen(true)}
                        aria-expanded={menuOpen}
                        aria-controls={MOBILE_MENU_ID}
                        className={cn(
                            'inline-flex size-10 items-center justify-center rounded-md text-mk-ink transition-colors duration-300 ease-mk hover:bg-mk-ink/[0.04] lg:hidden',
                            FOCUS_RING,
                        )}
                    >
                        <span className="sr-only">{t('site.nav.openMenu')}</span>
                        <MenuIcon open={menuOpen} />
                    </button>
                </div>
            </div>

            <MobileSheet
                open={menuOpen}
                onClose={() => setMenuOpen(false)}
                t={t}
                homeHref={homeHref}
                primary={primary}
                resources={resources}
                loginHref={loginHref}
                action={actionLink}
                currentPath={currentPath}
            />
        </header>
    );
}

/**
 * Resources: a disclosure button and a small panel of links. Opens on hover
 * (mouse only, so a tap does not open and then immediately toggle shut) and on
 * click or Enter/Space; closes on Escape, an outside press, or focus leaving.
 */
function ResourcesMenu({ label, items, currentPath }) {
    const [open, setOpen] = useState(false);
    const openedBy = useRef(null);
    const closeTimer = useRef(null);
    const rootRef = useRef(null);
    const buttonRef = useRef(null);
    const hasCurrent = items.some(item => isCurrent(item.href, currentPath));

    const cancelClose = () => {
        clearTimeout(closeTimer.current);
        closeTimer.current = null;
    };

    const show = how => {
        cancelClose();
        if (!openedBy.current) openedBy.current = how;
        setOpen(true);
    };

    const hide = () => {
        cancelClose();
        openedBy.current = null;
        setOpen(false);
    };

    useEffect(() => cancelClose, []);

    // Document listeners exist only while the panel is open.
    useEffect(() => {
        if (!open) return undefined;

        const handleKeyDown = event => {
            if (event.key !== 'Escape') return;
            const focusInside = rootRef.current?.contains(document.activeElement);
            hide();
            if (focusInside) buttonRef.current?.focus();
        };

        const handlePointerDown = event => {
            if (!rootRef.current?.contains(event.target)) hide();
        };

        document.addEventListener('keydown', handleKeyDown);
        document.addEventListener('pointerdown', handlePointerDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener('pointerdown', handlePointerDown);
        };
    }, [open]);

    const handleClick = () => {
        // A mouse user who hovered it open and then clicks means "keep it open".
        if (open && openedBy.current === 'hover') {
            openedBy.current = 'click';
            return;
        }

        if (open) hide();
        else show('click');
    };

    return (
        <div
            ref={rootRef}
            className="relative"
            onPointerEnter={event => {
                if (event.pointerType === 'mouse') show('hover');
            }}
            onPointerLeave={event => {
                if (event.pointerType !== 'mouse' || openedBy.current !== 'hover') return;
                cancelClose();
                closeTimer.current = setTimeout(hide, HOVER_CLOSE_DELAY);
            }}
            onBlur={event => {
                if (!rootRef.current?.contains(event.relatedTarget)) hide();
            }}
        >
            <button
                ref={buttonRef}
                type="button"
                aria-expanded={open}
                aria-controls={RESOURCES_MENU_ID}
                onClick={handleClick}
                className={cn(NAV_ITEM_BASE, NAV_ITEM[hasCurrent || open ? 'current' : 'rest'], FOCUS_RING)}
            >
                {label}
                <ChevronDown
                    aria-hidden="true"
                    strokeWidth={1.5}
                    className={cn(
                        'size-4 transition-transform duration-300 ease-mk motion-reduce:transition-none',
                        open && 'rotate-180',
                    )}
                />
            </button>

            {/* pt-3 rather than a margin: the gap stays part of the hover area. */}
            <div
                id={RESOURCES_MENU_ID}
                className={cn(
                    'absolute left-1/2 top-full w-96 -translate-x-1/2 pt-3',
                    'transition-[opacity,transform,visibility] duration-300 ease-mk motion-reduce:transition-none',
                    open
                        ? 'visible translate-y-0 opacity-100'
                        : 'pointer-events-none invisible -translate-y-1 opacity-0 motion-reduce:translate-y-0',
                )}
            >
                {/* Padded, its options a gap apart (ask 12): each ring clears its neighbours. */}
                <ul role="list" className="flex flex-col gap-1.5 rounded-xl bg-mk-surface p-2 shadow-mk-lift ring-1 ring-mk-line">
                    {items.map(item => {
                        const current = isCurrent(item.href, currentPath);

                        return (
                            <li key={item.key}>
                                <SmartLink
                                    href={item.href}
                                    aria-current={current ? 'page' : undefined}
                                    onClick={hide}
                                    className={cn(
                                        'block min-h-10 rounded-lg px-4 py-3 transition-colors duration-300 ease-mk hover:bg-mk-sunken',
                                        current && 'bg-mk-sunken',
                                        FOCUS_RING,
                                        'focus-visible:ring-offset-mk-surface',
                                    )}
                                >
                                    <span className="block text-sm font-medium text-mk-ink">{item.label}</span>
                                    <span className="mt-0.5 block text-sm text-mk-subtle">{item.description}</span>
                                </SmartLink>
                            </li>
                        );
                    })}
                </ul>
            </div>
        </div>
    );
}

/**
 * Two strokes that sit as a hamburger and rotate into an X. `animateIn` makes a
 * freshly mounted open icon start from the hamburger, so the sheet's close
 * button reads as the same icon morphing.
 */
function MenuIcon({ open, animateIn = false }) {
    const reduceMotion = useReducedMotion();
    const transition = reduceMotion ? { duration: 0 } : { duration: 0.45, ease: EASE_MK };
    const closed = [{ y: -4, rotate: 0 }, { y: 4, rotate: 0 }];
    const target = open ? [{ y: 0, rotate: 45 }, { y: 0, rotate: -45 }] : closed;
    const stroke = 'absolute inset-x-0.5 top-1/2 -mt-px h-[1.5px] rounded-full bg-current';

    return (
        <span aria-hidden="true" className="relative block size-5">
            {target.map((state, index) => (
                <motion.span
                    key={index}
                    className={stroke}
                    initial={animateIn && !reduceMotion ? closed[index] : false}
                    animate={state}
                    transition={transition}
                />
            ))}
        </span>
    );
}

/**
 * One entry of the mobile sheet (an <li> unless `as` says otherwise), fading
 * up 60ms after the one before it.
 */
function SheetItem({ as: Tag = 'li', index, className, children, ...props }) {
    const reduceMotion = useReducedMotion();

    if (reduceMotion) {
        return (
            <Tag className={className} {...props}>
                {children}
            </Tag>
        );
    }

    const MotionTag = motion[Tag];

    return (
        <MotionTag
            className={className}
            {...props}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: EASE_MK, delay: 0.08 + index * 0.06 }}
        >
            {children}
        </MotionTag>
    );
}

/**
 * The full-screen mobile menu. Its top row repeats the bar's geometry, so the
 * logo stays put and the hamburger appears to turn into the close button.
 * Headless UI supplies the focus trap, Escape and the body scroll lock.
 */
function MobileSheet({ open, onClose, t, homeHref, primary, resources, loginHref, action, currentPath }) {
    let index = 0;

    return (
        <Dialog open={open} onClose={onClose} id={MOBILE_MENU_ID} className="relative z-50 lg:hidden">
            <DialogPanel
                transition
                className={cn(
                    'fixed inset-0 overflow-y-auto',
                    'transition-opacity duration-300 ease-mk data-[closed]:opacity-0 motion-reduce:transition-none',
                    SHEET,
                )}
            >
                <DialogTitle className="sr-only">{t('site.nav.menu')}</DialogTitle>

                <div className="px-4 pt-4 sm:px-6">
                    <div className="mx-auto flex h-16 max-w-[1180px] items-center justify-between pl-5 pr-3">
                        <SmartLink
                            href={homeHref}
                            onClick={onClose}
                            aria-label={t('site.nav.home')}
                            className={cn('-mx-1.5 rounded-md px-1.5 py-1', FOCUS_RING)}
                        >
                            <Logo className={LOGO_SIZE} />
                        </SmartLink>
                        {/* Takes the initial focus: the X lands where the hamburger was. */}
                        <button
                            type="button"
                            onClick={onClose}
                            data-autofocus
                            className={cn(
                                'inline-flex size-10 items-center justify-center rounded-md transition-colors duration-300 ease-mk hover:bg-mk-ink/[0.04]',
                                FOCUS_RING,
                            )}
                        >
                            <span className="sr-only">{t('site.nav.closeMenu')}</span>
                            <MenuIcon open animateIn />
                        </button>
                    </div>
                </div>

                <div className="px-4 pb-12 pt-8 sm:px-6">
                    <div className="mx-auto max-w-[1180px] px-5">
                        <nav aria-label={t('site.nav.label')}>
                            <ul role="list" className="flex flex-col gap-1">
                                {primary.map(item => (
                                    <SheetItem key={item.key} index={index++}>
                                        <SmartLink
                                            href={item.href}
                                            onClick={onClose}
                                            aria-current={sectionCurrent(item.href, currentPath)}
                                            className={cn(
                                                'inline-block rounded-lg py-1.5 text-3xl font-semibold tracking-[-0.02em] text-mk-ink transition-colors duration-300 ease-mk hover:text-mk-body',
                                                'aria-[current=page]:text-mk-orange aria-[current=true]:text-mk-orange',
                                                FOCUS_RING,
                                            )}
                                        >
                                            {item.label}
                                        </SmartLink>
                                    </SheetItem>
                                ))}
                            </ul>

                            <SheetItem
                                as="p"
                                index={index++}
                                id="marketing-sheet-resources"
                                className="mb-3 mt-10 text-sm font-medium text-mk-subtle"
                            >
                                {t('site.nav.resources')}
                            </SheetItem>
                            <ul role="list" aria-labelledby="marketing-sheet-resources" className="flex flex-col gap-1">
                                {resources.map(item => (
                                    <SheetItem key={item.key} index={index++}>
                                        <SmartLink
                                            href={item.href}
                                            onClick={onClose}
                                            aria-current={isCurrent(item.href, currentPath) ? 'page' : undefined}
                                            className={cn(
                                                'inline-block rounded-lg py-1.5 text-xl font-medium text-mk-ink transition-colors duration-300 ease-mk hover:text-mk-body',
                                                'aria-[current=page]:text-mk-orange',
                                                FOCUS_RING,
                                            )}
                                        >
                                            {item.label}
                                        </SmartLink>
                                    </SheetItem>
                                ))}
                            </ul>
                        </nav>

                        <ul role="list" className="mt-12 flex flex-wrap gap-3">
                            <SheetItem index={index++}>
                                <Button href={action.href} size="lg" onClick={onClose}>
                                    {action.label}
                                </Button>
                            </SheetItem>
                            {loginHref && (
                                <SheetItem index={index++}>
                                    <Button
                                        href={loginHref}
                                        size="lg"
                                        variant="secondary"
                                        icon={null}
                                        onClick={onClose}
                                    >
                                        {t('site.nav.login')}
                                    </Button>
                                </SheetItem>
                            )}
                        </ul>
                    </div>
                </div>
            </DialogPanel>
        </Dialog>
    );
}
